import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pill, Trash2, Edit, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const EMPTY = {
  generic_name: "", brand_names: "", drug_class: "", allergy_class: "", ingredients: "",
  routes: "", common_doses: "", default_frequency: "", form: "", interaction_classes: "",
  high_alert: false, active: true, notes: "",
};

const toForm = (d) => ({
  generic_name: d.generic_name || "", brand_names: (d.brand_names || []).join(", "),
  drug_class: d.drug_class || "", allergy_class: d.allergy_class || "",
  ingredients: (d.ingredients || []).join(", "), routes: (d.routes || []).join(", "),
  common_doses: (d.common_doses || []).join(", "), default_frequency: d.default_frequency || "",
  form: d.form || "", interaction_classes: (d.interaction_classes || []).join(", "),
  high_alert: !!d.high_alert, active: d.active !== false, notes: d.notes || "",
});

const csv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function DrugFormulary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sheet, setSheet] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState({ open: false });

  async function load() {
    setLoading(true);
    try { const r = await api.get("/drug-formulary"); setItems(r.data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((d) => `${d.generic_name} ${(d.brand_names || []).join(" ")} ${d.drug_class || ""}`.toLowerCase().includes(q));
  }, [items, search]);

  function openNew() { setEditing(null); setForm(EMPTY); setSheet(true); }
  function openEdit(d) { setEditing(d); setForm(toForm(d)); setSheet(true); }

  async function save() {
    if (!form.generic_name.trim()) { toast.error("Generic name is required"); return; }
    setSaving(true);
    const payload = {
      generic_name: form.generic_name.trim(), brand_names: csv(form.brand_names),
      drug_class: form.drug_class.trim() || null, allergy_class: form.allergy_class.trim() || null,
      ingredients: csv(form.ingredients), routes: csv(form.routes), common_doses: csv(form.common_doses),
      default_frequency: form.default_frequency.trim() || null, form: form.form.trim() || null,
      interaction_classes: csv(form.interaction_classes), high_alert: form.high_alert,
      active: form.active, notes: form.notes.trim() || null,
    };
    try {
      if (editing) await api.put(`/drug-formulary/${editing.id}`, payload);
      else await api.post("/drug-formulary", payload);
      toast.success(editing ? "Drug updated" : "Drug added");
      setSheet(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  async function doDelete(d) {
    try { await api.delete(`/drug-formulary/${d.id}`); toast.success("Drug removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setConfirm({ open: false }); }
  }

  return (
    <div data-testid="drug-formulary-page">
      <PageHeader
        title="Drug Formulary"
        subtitle="Curated medication catalog powering structured prescribing, allergy cross-reactivity and drug-drug interaction checks."
        actions={
          <Button onClick={openNew} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="drug-new-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Drug
          </Button>
        }
      />

      <div className="hah-card p-3 mb-3 flex items-center gap-2">
        <Search className="w-4 h-4 text-[#94A3B8]" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, brand or class…"
               data-testid="drug-search" className="flex-1 bg-transparent text-sm outline-none" />
        <span className="text-xs text-[#94A3B8]">{filtered.length} drug(s)</span>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-10 text-center text-sm text-[#6B7280]" data-testid="drug-empty">No drugs found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((d) => (
            <div key={d.id} data-testid={`drug-card-${d.id}`} className="hah-card p-4 flex flex-col gap-2 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[15px] font-semibold text-[#1F2937] truncate flex items-center gap-1.5">
                    <Pill className="w-4 h-4 text-[#8B5CF6] shrink-0" /> {d.generic_name}
                  </div>
                  {(d.brand_names || []).length > 0 && <div className="text-[11px] text-[#94A3B8] truncate">{d.brand_names.join(", ")}</div>}
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {!d.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F1F5F9] text-[#64748B] font-medium">Inactive</span>}
                  {d.high_alert && <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#991B1B] font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />High-alert</span>}
                </div>
              </div>
              {d.drug_class && <div className="text-[12px] text-[#6B7280]">{d.drug_class}</div>}
              <div className="flex flex-wrap gap-1.5">
                {d.allergy_class && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#FEF3C7] text-[#92400E]">allergy: {d.allergy_class}</span>}
                {(d.interaction_classes || []).map((c, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#EEF2FF] text-[#4338CA]">{c}</span>
                ))}
              </div>
              <div className="text-[12px] text-[#6B7280] mt-auto pt-1">
                {(d.routes || []).join(" · ")}{(d.common_doses || []).length ? ` — ${d.common_doses.join(", ")}` : ""}{d.default_frequency ? ` · ${d.default_frequency}` : ""}
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-[#F1F5F9]">
                <button onClick={() => openEdit(d)} data-testid={`drug-edit-${d.id}`} className="text-[12px] text-[#0B7E8C] hover:underline inline-flex items-center gap-1"><Edit className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => setConfirm({ open: true, item: d })} data-testid={`drug-delete-${d.id}`} className="text-[12px] text-[#EF4444] hover:underline inline-flex items-center gap-1 ml-auto"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Sheet open={sheet} onOpenChange={setSheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="drug-sheet">
          <SheetHeader>
            <SheetTitle>{editing ? "Edit Drug" : "New Drug"}</SheetTitle>
            <SheetDescription>Define the drug and the keys used for allergy cross-reactivity and interaction checks.</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-5">
            <Field label="Generic Name *"><Input value={form.generic_name} onChange={(e) => setForm({ ...form, generic_name: e.target.value })} placeholder="Amoxicillin" data-testid="drug-form-generic" /></Field>
            <Field label="Brand Names (comma-separated)"><Input value={form.brand_names} onChange={(e) => setForm({ ...form, brand_names: e.target.value })} placeholder="Amoxil, Moxatag" data-testid="drug-form-brands" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Drug Class"><Input value={form.drug_class} onChange={(e) => setForm({ ...form, drug_class: e.target.value })} placeholder="Penicillin antibiotic" /></Field>
              <Field label="Allergy Class"><Input value={form.allergy_class} onChange={(e) => setForm({ ...form, allergy_class: e.target.value })} placeholder="penicillin" data-testid="drug-form-allergy-class" /></Field>
            </div>
            <Field label="Active Ingredients (comma-separated)"><Input value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} placeholder="amoxicillin" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Routes (comma-separated)"><Input value={form.routes} onChange={(e) => setForm({ ...form, routes: e.target.value })} placeholder="PO, IV" /></Field>
              <Field label="Form"><Input value={form.form} onChange={(e) => setForm({ ...form, form: e.target.value })} placeholder="capsule" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Common Doses (comma-separated)"><Input value={form.common_doses} onChange={(e) => setForm({ ...form, common_doses: e.target.value })} placeholder="250 mg, 500 mg" /></Field>
              <Field label="Default Frequency"><Input value={form.default_frequency} onChange={(e) => setForm({ ...form, default_frequency: e.target.value })} placeholder="TID" /></Field>
            </div>
            <Field label="Interaction Classes (comma-separated)"><Input value={form.interaction_classes} onChange={(e) => setForm({ ...form, interaction_classes: e.target.value })} placeholder="warfarin, anticoagulant" data-testid="drug-form-interaction" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="High-alert">
                <Select value={form.high_alert ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, high_alert: v === "yes" })}>
                  <SelectTrigger data-testid="drug-form-highalert"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="no">No</SelectItem><SelectItem value="yes">Yes</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Active">
                <Select value={form.active ? "yes" : "no"} onValueChange={(v) => setForm({ ...form, active: v === "yes" })}>
                  <SelectTrigger data-testid="drug-form-active"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="yes">Active</SelectItem><SelectItem value="no">Inactive</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
            <Field label="Notes"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" /></Field>
          </div>
          <div className="flex items-center gap-2 mt-6 pt-4 border-t border-[#E5E7EB]">
            <Button onClick={save} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="drug-save-btn">
              {saving ? "Saving…" : editing ? "Save changes" : "Add drug"}
            </Button>
            <Button variant="outline" onClick={() => setSheet(false)} className="border-[#E5E7EB]">Cancel</Button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => setConfirm({ ...confirm, open: o })}
        title="Delete drug?"
        description={`"${confirm.item?.generic_name}" will be permanently removed from the formulary.`}
        confirmLabel="Delete" destructive
        onConfirm={() => doDelete(confirm.item)}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[12px] font-medium text-[#374151] mb-1">{label}</label>
      {children}
    </div>
  );
}
